/**
 * Centralized Cart Refresh System
 * Ensures cart drawer, bubble, and progress bar all update together
 */

/* ------------------------------------------------------------------
 * Serialise cart WRITES. Concurrent POSTs to /cart/add|change|update can
 * make Shopify create duplicate line items — it only merges same-variant
 * lines when the adds are processed one at a time. The theme fires cart
 * writes from several uncoordinated places (product form, drawer quick-add,
 * and the reward engine adding gifts), so a fast tap while the engine is
 * mutating can collide. We queue every cart-write fetch so at most one is in
 * flight; reads (/cart.js, ?sections=) and every other request pass straight
 * through untouched. Runs before any interaction can occur (this file is a
 * render-blocking script), and wraps window.fetch once.
 * ------------------------------------------------------------------ */
(function () {
  if (window.__mercyCartSerialised) return;
  window.__mercyCartSerialised = true;
  // pending = number of cart-write requests currently in flight. The reward
  // engine reads this so it only de-duplicates when the cart is settled.
  window.MercyCart = window.MercyCart || {};
  window.MercyCart.pending = 0;
  var nativeFetch = window.fetch.bind(window);
  var tail = Promise.resolve();
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }
  window.fetch = function (input, init) {
    if (!CART_WRITE.test(urlOf(input))) {
      return nativeFetch(input, init);
    }
    window.MercyCart.pending++;
    var args = arguments;
    var settle = function () { if (window.MercyCart.pending > 0) { window.MercyCart.pending--; } };
    var run = function () { return nativeFetch.apply(null, args); };
    var p = tail.then(run, run);            // wait for the previous write (success OR failure)
    p.then(settle, settle);                 // decrement pending when this write settles
    // Advance the queue after this write settles, but never let a hung/failed
    // request block the queue forever (10s safety valve).
    tail = Promise.race([
      p.then(function () {}, function () {}),
      new Promise(function (resolve) { setTimeout(resolve, 10000); })
    ]);
    return p;
  };
})();

window.refreshCart = async function() {
  console.log('🔄 Refreshing cart...');

  try {
    // Fetch cart data and sections in parallel
    const [cartResponse, sectionsResponse] = await Promise.all([
      fetch('/cart.js'),
      fetch(`${window.location.pathname}?sections=cart-drawer,cart-bubble`)
    ]);

    const cart = await cartResponse.json();
    const sections = await sectionsResponse.json();

    console.log('Cart data fetched:', { itemCount: cart.item_count, total: cart.total_price });

    // Update cart drawer
    if (sections['cart-drawer']) {
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
        const newInner = doc.querySelector('.side-panel-inner');
        const currentInner = cartDrawer.querySelector('.side-panel-inner');

        if (newInner && currentInner) {
          console.log('✅ Updating cart drawer content');
          currentInner.replaceWith(newInner);
          // Re-bind the per-element handlers the theme re-runs after its own
          // renders. Custom elements (<quantity-selector> etc.) self-init on
          // insert, but the .remove links + note toggle are bound per-element in
          // app.js — without this, a tapped Remove link falls back to its
          // <a href="/cart/change?…"> and full-navigates to /cart.
          if (cartDrawer && typeof cartDrawer.removeProductEvent === 'function') {
            cartDrawer.removeProductEvent();
          }
          if (cartDrawer && typeof cartDrawer.notesToggle === 'function') {
            cartDrawer.notesToggle();
          }
        } else {
          console.error('❌ Could not find drawer elements:', { newInner, currentInner });
        }
      }
    }

    // Update cart bubble
    if (sections['cart-bubble']) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(sections['cart-bubble'], 'text/html');
      const newBubble = doc.querySelector('.thb-item-count');
      const currentBubble = document.querySelector('.thb-item-count');

      if (newBubble && currentBubble) {
        console.log('✅ Updating cart bubble');
        currentBubble.replaceWith(newBubble);
      }
    }

    // Update progress bar with fresh cart total
    const progressBar = document.querySelector('unified-progress-bar');
    if (progressBar) {
      console.log('✅ Updating progress bar with total:', cart.total_price);
      progressBar.dataset.cartTotal = cart.total_price;
      if (typeof progressBar.updateProgress === 'function') {
        progressBar.updateProgress();
      }
    }

    // Dispatch completion event
    document.dispatchEvent(new CustomEvent('cart:refreshed', {
      detail: { cart }
    }));

    console.log('✅ Cart refresh complete');
    return cart;

  } catch (error) {
    console.error('❌ Error refreshing cart:', error);
    throw error;
  }
};

/* ------------------------------------------------------------------
 * Drawer-first guard — the cart is the drawer, never the /cart page.
 * The cart icon is an <a href="/cart"> for no-JS fallback, and the core
 * cart-drawer element binds its own click handler once it upgrades. This
 * capture-phase listener runs from first script execution, so a click on
 * the cart icon (or any /cart link) opens the drawer instead of navigating
 * — even before the custom element has hydrated. If the drawer isn't on the
 * page (e.g. disabled in settings) we let the link behave normally.
 * ------------------------------------------------------------------ */
(function () {
  var CART_TOGGLE = '#cart-drawer-toggle, .thb-secondary-cart, .thb-secondary-item[href$="/cart"], a[href$="/cart"]';

  function openDrawer() {
    var drawer = document.getElementById('Cart-Drawer');
    if (!drawer) return false;
    drawer.classList.add('active');
    document.body.classList.add('open-cart');
    document.body.classList.add('open-cc');
    try { drawer.focus(); } catch (e) {}
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));
    return true;
  }

  function toggleFrom(e) {
    return (e.target && e.target.closest) ? e.target.closest(CART_TOGGLE) : null;
  }

  // click is the reliable navigation blocker (preventDefault stops the <a>).
  document.addEventListener('click', function (e) {
    if (!toggleFrom(e)) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return; // allow open-in-new-tab
    if (openDrawer()) { e.preventDefault(); }
  }, true);

  // pointerdown opens the drawer the instant a finger/cursor lands, so on
  // mobile the drawer is already opening before any link activation — kills the
  // intermittent "tap navigates to /cart" flash. Guarded to primary button /
  // touch / pen only; the click handler above still prevents the navigation.
  document.addEventListener('pointerdown', function (e) {
    if (e.button && e.button > 0) return;
    if (!toggleFrom(e)) return;
    openDrawer();
  }, true);
})();
